import { MailOutlined, SendOutlined } from '@ant-design/icons'
import { useMemo, useState } from 'react'

import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import ANTDButton from '../../../shared/antd/ANTDButton'
import ANTDCollapse, { ANTDPanel } from '../../../shared/antd/ANTDCollapse'
import { ANTDDatePicker } from '../../../shared/antd/ANTDDatePicker'
import ANTDForm, {
  ANTDFormItem,
  useFormFn,
} from '../../../shared/antd/ANTDForm'
import ANTDInput, { ANTDTextArea } from '../../../shared/antd/ANTDInput'
import ANTDModal from '../../../shared/antd/ANTDModal'
import ANTDSelect from '../../../shared/antd/ANTDSelect'
import PopUpConfirm from '../../../shared/PopUpConfirm'
import { getReminderTemplates } from '../email.description'
import { priorityOptions } from '../user.description'

const getReminderEmail = userDetails => {
  const primaryEmail = Object.entries(userDetails || {}).find(
    ([key, value]) => key.includes('emailId') && value,
  )?.[1]

  return (
    primaryEmail ||
    userDetails?.inchargeEmail ||
    userDetails?.parentDetails?.emailId
  )
}

const getHtmlBody = body =>
  String(body || '')
    .split('\n')
    .filter(line => line.trim())
    .map(line => `<p>${line}</p>`)
    .join('')

const SendReminderModal = ({
  open,
  userDetails,
  loading,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslations()
  const { selector } = useRedux()
  const form = useFormFn()
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [templateOverwriteConfirm, setTemplateOverwriteConfirm] = useState({
    open: false,
    template: null,
  })
  const [templateForm, setTemplateForm] = useState({
    jobId: '',
    batchNumber: '',
    invoiceNumber: '',
    reportName: '',
  })
  const emailId = getReminderEmail(userDetails)
  const adminDetails = selector(state => state.user?.profile_details) || {}

  const templateList = useMemo(
    () => getReminderTemplates({ t, userDetails, adminDetails }),
    [adminDetails, t, userDetails],
  )

  const handleFinish = values => {
    onSubmit({
      ...values,
      body: getHtmlBody(values?.body),
      userId: userDetails?.id,
      deadLineDate: values?.deadLineDate?.format('DD/MM/YYYY'),
    })
  }

  const handleTemplateFieldChange = (key, value) =>
    setTemplateForm(prev => ({ ...prev, [key]: value }))

  const applyTemplate = template => {
    form.setFieldsValue({
      subject: template.getSubject(templateForm),
      body: template.getBody(templateForm),
    })
    setTemplateModalOpen(false)
  }

  const handleUseTemplate = template => {
    const subject = form.getFieldValue('subject')
    const body = form.getFieldValue('body')
    const hasExistingContent = Boolean(subject?.trim?.() || body?.trim?.())

    if (hasExistingContent) {
      setTemplateOverwriteConfirm({ open: true, template })
      return
    }
    applyTemplate(template)
  }

  const handleTemplateOverwriteProceed = () => {
    if (templateOverwriteConfirm?.template) {
      applyTemplate(templateOverwriteConfirm.template)
    }
    setTemplateOverwriteConfirm({ open: false, template: null })
  }

  const handleTemplateOverwriteCancel = () => {
    setTemplateOverwriteConfirm({ open: false, template: null })
    setTemplateModalOpen(false)
  }

  return (
    <ANTDModal
      title={null}
      centered
      open={open}
      onCancel={onCancel}
      footer={false}
      width={760}
      className="email-compose-modal"
    >
      <div className="email-template-wrapper">
        <div className="email-compose-header">
          <div className="email-compose-icon">
            <MailOutlined />
          </div>
          <div>
            <h3>{t('user_reminderModalTitle')}</h3>
            <p>{t('user_reminderModalSubtitle')}</p>
          </div>
          <ANTDButton
            className="email-template-trigger cursor-pointer"
            onClick={() => setTemplateModalOpen(true)}
          >
            {t('txt_Templates')}
          </ANTDButton>
        </div>

        <ANTDForm
          form={form}
          onFinish={handleFinish}
          initialValues={{ priority: 'REMINDER' }}
          requiredMark={false}
        >
          <div className="email-compose-shell">
            <div className="email-compose-row">
              <span className="email-compose-label">
                {t('user_reminderTo')}
              </span>
              <span className="email-compose-recipient">{emailId || '-'}</span>
            </div>

            <ANTDFormItem
              className="email-compose-subject"
              name="subject"
              rules={[
                { required: true, message: t('user_reminderSubjectRequired') },
              ]}
            >
              <ANTDInput placeholder={t('user_reminderSubjectPlaceholder')} />
            </ANTDFormItem>

            <ANTDFormItem
              className="email-compose-body"
              name="body"
              rules={[
                { required: true, message: t('user_reminderBodyRequired') },
              ]}
            >
              <ANTDTextArea
                rows={10}
                placeholder={t('user_reminderBodyPlaceholder')}
              />
            </ANTDFormItem>
          </div>

          <div className="email-template-footer">
            <div className="email-template-grid">
              <ANTDFormItem
                label={t('user_reminderDeadline')}
                name="deadLineDate"
                rules={[
                  {
                    required: true,
                    message: t('user_reminderDeadlineRequired'),
                  },
                ]}
              >
                <ANTDDatePicker className="w-100" format="DD/MM/YYYY" />
              </ANTDFormItem>

              <ANTDFormItem
                label={t('user_reminderPriority')}
                name="priority"
                rules={[
                  {
                    required: true,
                    message: t('user_reminderPriorityRequired'),
                  },
                ]}
              >
                <ANTDSelect options={priorityOptions(t)} />
              </ANTDFormItem>
            </div>
          </div>

          <div className="email-template-actions">
            <ANTDButton
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SendOutlined />}
            >
              {t('user_reminderSubmit')}
            </ANTDButton>
          </div>
        </ANTDForm>
      </div>
      <ANTDModal
        title={t('txt_Templates')}
        centered
        open={templateModalOpen}
        onCancel={() => setTemplateModalOpen(false)}
        footer={false}
        width={900}
        className="reminder-template-modal"
      >
        <div className="reminder-template-list">
          <ANTDCollapse accordion defaultActiveKey={[]}>
            {templateList.map(template => (
              <ANTDPanel
                header={template.title}
                key={template.key}
                className="reminder-template-panel"
              >
                <div className="reminder-template-content">
                  {template?.fields?.length ? (
                    <div className="reminder-template-fields">
                      {template.fields.map(field => (
                        <div
                          key={field.key}
                          className="reminder-template-field-item"
                        >
                          <label>{field.label}</label>
                          <ANTDInput
                            value={templateForm?.[field.key]}
                            placeholder={field.placeholder}
                            onChange={e =>
                              handleTemplateFieldChange(
                                field.key,
                                e?.target?.value,
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="reminder-template-preview">
                    <div className="reminder-template-preview-block">
                      <span className="reminder-template-preview-label">
                        {t('issue_Subject')}
                      </span>
                      <p>{template.getSubject(templateForm)}</p>
                    </div>
                    <div className="reminder-template-preview-block">
                      <span className="reminder-template-preview-label">
                        {t('inv_Body')}
                      </span>
                      <pre>{template.getBody(templateForm)}</pre>
                    </div>
                  </div>

                  <div className="reminder-template-use-action">
                    <ANTDButton
                      type="primary"
                      onClick={() => handleUseTemplate(template)}
                    >
                      {t('txt_UseTemplate')}
                    </ANTDButton>
                  </div>
                </div>
              </ANTDPanel>
            ))}
          </ANTDCollapse>
        </div>
      </ANTDModal>
      <PopUpConfirm
        isOpen={templateOverwriteConfirm.open}
        onCancelModel={handleTemplateOverwriteCancel}
        onAccept={handleTemplateOverwriteProceed}
        onReject={handleTemplateOverwriteCancel}
        acceptLabel="btn_Proceed"
        rejectLabel="msg_Cancel"
        description={t('txt_UseTemplateConfirmation')}
      />
    </ANTDModal>
  )
}

export default SendReminderModal
